
import { ABSTRACT_ROLES } from '../RoleAllocator.js';
import { NOTES, normalizeKey } from '../../data/theoryConstants.js';

export class FidelityAnalyzer {
    constructor() {
        this.metrics = {};
    }

    /**
     * Audits a generated session against the target genre archetypes.
     * @param {Array} events - The generated MIDI events.
     * @param {Object} context - The generation context (instruments, structure, etc).
     * @param {Object} archetype - The target genre archetype (e.g. PASTORAL_ARCHETYPE).
     */
    audit(events, context, archetype) {
        const report = {
            totalEvents: events.length,
            instruments: {},
            globalIssues: []
        };

        // Group events by Instrument ID and Role
        const byInstrument = {};
        events.forEach(ev => {
            const key = ev.instrumentId || ev.role || 'unknown';
            if (!byInstrument[key]) byInstrument[key] = { events: [], role: ev.role, id: ev.instrumentId };
            byInstrument[key].events.push(ev);
        });

        // Analyze each instrument
        Object.values(byInstrument).forEach(entry => {
            const { events, role, id } = entry;
            const instDef = context.instruments.find(i => i.id === id);

            const analysis = {
                role: role,
                eventCount: events.length,
                rhythm: this.analyzeRhythm(events, context.timeSig || 4, archetype),
                dynamics: this.analyzeDynamics(events, archetype),
                pitch: this.analyzePitch(events, role, instDef),
                issues: []
            };

            // Heuristic Checks
            // Lowered threshold to 0.01 (StdDev ~0.1, Range ~0.35) which is sufficient for "Human" feel.
            // Exception: If archetype explicitly allows near-zero variance (Minimalist, Impressionist).
            const allowedMinVariance = (archetype && archetype.dynamicRange) ? archetype.dynamicRange.minVariance : 0.05;

            if (analysis.dynamics.variance < 0.01 && events.length > 10) {
                if (allowedMinVariance > 0.0) {
                    analysis.issues.push('FLATLINE_DYNAMICS');
                }
            }

            if (role === ABSTRACT_ROLES.BASS) {
                if (analysis.pitch.avg > 60) analysis.issues.push('BASS_TOO_HIGH');
            }

            // Horror Specific: Check if dynamics are TOO consistent (Variance < 0.05 implies no spikes)
            if (archetype.name === 'Horror' && analysis.dynamics.variance < 0.05 && events.length > 10) {
                 // Horror expects spikes (stings).
            }

            report.instruments[id || role] = analysis;
        });

        return report;
    }

    analyzeRhythm(events, timeSig, archetype) {
        if (events.length < 2) return { gridAdherence: 1.0, swing: 0 };

        // Calculate offsets from nearest 1/16th note
        let totalDeviation = 0;
        let onGridCount = 0;
        const grid = 0.25; // 16th note

        events.forEach(ev => {
            const s = ev.s;
            const nearest = Math.round(s / grid) * grid;
            const diff = Math.abs(s - nearest);
            totalDeviation += diff;
            if (diff < 0.01) onGridCount++;
        });

        return {
            gridAdherence: onGridCount / events.length,
            avgDeviation: totalDeviation / events.length
        };
    }

    analyzeDynamics(events, archetype) {
        if (events.length === 0) return { min: 0, max: 0, avg: 0, variance: 0 };

        const velocities = events.map(e => e.vel || 0);
        const min = Math.min(...velocities);
        const max = Math.max(...velocities);
        const avg = velocities.reduce((a, b) => a + b, 0) / velocities.length;

        // Variance
        const sqDiff = velocities.map(v => Math.pow(v - avg, 2));
        const variance = sqDiff.reduce((a, b) => a + b, 0) / velocities.length;

        return { min, max, avg, variance };
    }

    analyzePitch(events, role, instDef) {
        if (events.length === 0) return { min: 0, max: 0, avg: 0 };

        // Filter out non-pitched events
        if (role && role.includes('perc')) return { min: 0, max: 0, avg: 0 };

        const pitches = events.map(e => e.d).filter(d => typeof d === 'number');
        if (pitches.length === 0) return { min: 0, max: 0, avg: 0 };

        const min = Math.min(...pitches);
        const max = Math.max(...pitches);
        const avg = pitches.reduce((a, b) => a + b, 0) / pitches.length;

        return { min, max, avg };
    }
}
